/**
 * Lightweight JWT payload helpers (no signature verification).
 * The backend verifies signatures; the client only reads claims for routing.
 */

/**
 * Decode the payload section of a JWT without verifying the signature.
 * Returns null if the token is malformed.
 *
 * ⚠️  base64URL, not base64. `atob` rejects `-` and `_`, which JWT payloads use
 *     in place of `+` and `/` (RFC 7515 §2) — and which turn up as soon as a
 *     claim carries an email or a display name. Without the substitution this
 *     returns null for a perfectly good token, and every caller reads that as
 *     "malformed".
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/**
 * Extract the user ID (`sub` claim) from a JWT access token.
 * Returns null if the token is malformed or missing `sub`.
 */
export function getUserIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.sub !== 'string') return null;
  return payload.sub;
}
